/**
 * Copyright (c) 2019, Tidepool Project
 *
 * This program is free software; you can redistribute it and/or modify it under
 * the terms of the associated License, which is identical to the BSD 2-Clause
 * License as published by the Open Source Initiative at opensource.org.
 *
 * This program is distributed in the hope that it will be useful, but WITHOUT
 * ANY WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS
 * FOR A PARTICULAR PURPOSE. See the License for more details.
 *
 * You should have received a copy of the License along with this program; if
 * not, you can obtain one from Tidepool Project at tidepool.org.
 */

import React, { useEffect } from 'react';
import {
    Grid,
    TextField,
    Container,
    Select,
    MenuItem,
    OutlinedInput,
    FormControl,
    InputLabel,
    InputAdornment
} from '@material-ui/core';
import * as _ from '../../../data/constants.js';
var utils = require('../../../Utils.js');

// Page 1 of the form, collects all useful patient information regarding Patient bio and background, if a user already exists most of this information can be set
// pre filled in
const PatientInfoForm = props => {
    // scroll to top on mount
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // The following 5 lines are used to help properly display labels for the select inputs, they can be ignored
    const inputLabel = React.useRef(null);
    const [labelWidth, setLabelWidth] = React.useState(0);
    React.useEffect(() => {
        setLabelWidth(inputLabel.current.offsetWidth);
    }, []);

    return (
        <form onSubmit={props.handleSubmit}>
            <Container style={{ width: '80%' }}>
                <Grid container spacing={3}>
                    {/* firstName input */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            name="firstName"
                            variant="outlined"
                            required
                            fullWidth
                            label="First Name"
                            error={props.touched.firstName && Boolean(props.errors.firstName)}
                            helperText={props.touched.firstName ? props.errors.firstName : ''}
                            onChange={props.handleChange}
                            value={props.values.firstName}
                        />
                    </Grid>
                    {/* lastName input */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            variant="outlined"
                            required
                            fullWidth
                            label="Last Name"
                            name="lastName"
                            error={props.touched.lastName && Boolean(props.errors.lastName)}
                            helperText={props.touched.lastName ? props.errors.lastName : ''}
                            onChange={props.handleChange}
                            value={props.values.lastName}
                        />
                    </Grid>
                    {/* MRN number input */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            variant="outlined"
                            fullWidth
                            label="Medical Record Number"
                            name="mrn"
                            error={props.touched.mrn && Boolean(props.errors.mrn)}
                            helperText={props.touched.mrn ? props.errors.mrn : ''}
                            onChange={props.handleChange}
                            value={props.values.mrn}
                        />
                    </Grid>
                    {/* email address input */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            variant="outlined"
                            required
                            fullWidth
                            label="Email Address"
                            name="email"
                            type="email"
                            placeholder="Example: jill@jellyfish.com"
                            error={props.touched.email && Boolean(props.errors.email)}
                            helperText={props.touched.email ? props.errors.email : ''}
                            onChange={props.handleChange}
                            value={props.values.email}
                        />
                    </Grid>
                    {/* Year of Diagnosis input */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            variant="outlined"
                            fullWidth
                            label="Year of Diagnosis"
                            name="yearOfDiagnosis"
                            error={props.touched.yearOfDiagnosis && Boolean(props.errors.yearOfDiagnosis)}
                            helperText={props.touched.yearOfDiagnosis ? props.errors.yearOfDiagnosis : ''}
                            onChange={props.handleChange}
                            value={props.values.yearOfDiagnosis}
                        />
                    </Grid>
                    {/* Birthday input */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            variant="outlined"
                            required
                            fullWidth
                            label="Patient's Birthday"
                            name="birthday"
                            type="date"
                            InputLabelProps={{
                                shrink: true
                            }}
                            inputProps={utils.birthdayRange()}
                            error={props.touched.birthday && Boolean(props.errors.birthday)}
                            helperText={props.touched.birthday ? props.errors.birthday : ''}
                            onChange={props.handleChange}
                            value={props.values.birthday}
                        />
                    </Grid>
                    {/* Weight input */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            variant="outlined"
                            fullWidth
                            label="Patient Weight"
                            name="weight"
                            InputProps={{
                                endAdornment: <InputAdornment position="end">kg</InputAdornment>
                            }}
                            error={props.touched.weight && Boolean(props.errors.weight)}
                            helperText={props.touched.weight ? props.errors.weight : ''}
                            onChange={props.handleChange}
                            value={props.values.weight}
                        />
                    </Grid>
                    {/* Patient Sex select */}
                    <Grid item xs={12} sm={6}>
                        <FormControl variant="outlined" fullWidth>
                            <InputLabel ref={inputLabel} htmlFor="sex-picker">
                                Patient Sex
                            </InputLabel>
                            <Select
                                name="sex"
                                input={<OutlinedInput labelWidth={labelWidth} name="sex" id="sex-picker" />}
                                error={props.touched.sex && Boolean(props.errors.sex)}
                                onChange={props.handleChange}
                                value={props.values.sex}
                            >
                                <MenuItem value={'male'}>Male</MenuItem>
                                <MenuItem value={'female'}>Female</MenuItem>
                                <MenuItem value={'Prefer not to say'}>Prefer not to say</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    {/* Address 1 input */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            variant="outlined"
                            required
                            fullWidth
                            label="Patient Address"
                            name="street1Address"
                            placeholder="Example: 501 Diabetes Way"
                            error={props.touched.street1Address && Boolean(props.errors.street1Address)}
                            helperText={props.touched.street1Address ? props.errors.street1Address : ''}
                            onChange={props.handleChange}
                            value={props.values.street1Address}
                        />
                    </Grid>
                    {/* Address 2 input */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            variant="outlined"
                            fullWidth
                            name="street2Address"
                            label="Patient Address Opt. Line 2"
                            error={props.touched.street2Address && Boolean(props.errors.street2Address)}
                            helperText={props.touched.street2Address ? props.errors.street2Address : ''}
                            onChange={props.handleChange}
                            value={props.values.street2Address}
                        />
                    </Grid>
                    {/* City Input */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            variant="outlined"
                            required
                            fullWidth
                            name="cityAddress"
                            label="Patient City"
                            placeholder="Example: Palo Alto"
                            error={props.touched.cityAddress && Boolean(props.errors.cityAddress)}
                            helperText={props.touched.cityAddress ? props.errors.cityAddress : ''}
                            onChange={props.handleChange}
                            value={props.values.cityAddress}
                        />
                    </Grid>
                    {/* State selector */}
                    <Grid item xs={12} sm={6}>
                        <FormControl variant="outlined" fullWidth required>
                            <InputLabel
                                ref={inputLabel}
                                htmlFor="state-picker"
                                error={props.touched.stateAddress && Boolean(props.errors.stateAddress)}
                            >
                                Patient State
                            </InputLabel>
                            <Select
                                name="stateAddress"
                                input={<OutlinedInput labelWidth={labelWidth} name="stateAddress" id="state-picker" />}
                                error={props.touched.stateAddress && Boolean(props.errors.stateAddress)}
                                onChange={props.handleChange}
                                value={props.values.stateAddress}
                            >
                                {_.StatesArray.map((state, index) => {
                                    return (
                                        <MenuItem key={index} value={state}>
                                            {state}
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        </FormControl>
                    </Grid>
                    {/* Zipcode input */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            variant="outlined"
                            required
                            fullWidth
                            label="Patient Zip Code"
                            name="zipcodeAddress"
                            placeholder="Example: 94301"
                            error={props.touched.zipcodeAddress && Boolean(props.errors.zipcodeAddress)}
                            helperText={props.touched.zipcodeAddress ? props.errors.zipcodeAddress : ''}
                            onChange={props.handleChange}
                            value={props.values.zipcodeAddress}
                        />
                    </Grid>
                    {/* Patient Phone number input*/}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            variant="outlined"
                            required
                            fullWidth
                            name="phoneNumber"
                            label="Patient Phone Number"
                            placeholder="Example: (650) 123-4567"
                            error={props.touched.phoneNumber && Boolean(props.errors.phoneNumber)}
                            helperText={props.touched.phoneNumber ? props.errors.phoneNumber : ''}
                            onChange={props.handleChange}
                            value={props.values.phoneNumber}
                        />
                    </Grid>
                </Grid>
            </Container>
        </form>
    );
};

export default PatientInfoForm;
